import React, { useState, useRef, useEffect } from 'react';
import { Job, User, JobStatus, Expense } from '../types';
import { apiUrl } from '../api';
import ArbE from './ArbE';

interface Props {
  jobs?: Job[];
  user?: User | null;
  expenses?: Expense[];
  onAddExpense?: (expense: Expense) => void;
  googleConnected?: boolean;
}

interface Message {
  id: string;
  role: 'user' | 'model';
  text: string;
  pendingExpense?: {
    merchant: string;
    amount: number;
    category: string;
  };
}

const ArbEWidget: React.FC<Props> = ({ jobs = [], user, expenses = [], onAddExpense, googleConnected }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: '1', role: 'model', text: "Hey there! I'm Arb-E 🐒, your GreenTree assistant. How can I help you safely branch out today?" }
  ]);
  const [inputVal, setInputVal] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Track chat instance
  const systemInstructionRef = useRef<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      let roleContext = '';
      if (user?.staffRole === 'admin') {
        const pendingQuotes = jobs.filter(j => j.status === JobStatus.ESTIMATING || j.status === JobStatus.PENDING).length;
        roleContext = `The user is an Admin. They can manage all records. Context: There are currently ${pendingQuotes} pending quotes awaiting approval or scheduling.`;
      } else if (user?.staffRole === 'contractor' || user?.staffRole === 'employee') {
        roleContext = `The user is a ${user?.staffRole}. Focus heavily on 'Field Operations' and safety. Emphasize active jobs, required safety checklists, and explicitly warn them about current weather safety alerts (e.g. 20mph wind alert in effect for the valley).`;
      } else if (user?.role === 'client') {
        roleContext = `The user is a Client. Focus on providing updates about their upcoming services, explaining ISA standards simply, and encouraging them to approve estimates or submit new requests.`;
      }

      // Security Context
      const safetyContext = `CRITICAL ARB-E DIRECTIVE: NEVER store, ask for, or repeat sensitive data like credit card numbers or passwords. If logging financials, ONLY log the Vendor Name, Date, and Total Amount.`;

      // Financial Context summary for this month
      const currentMonth = new Date().toISOString().substring(0, 7);
      const monthlyOverhead = expenses
        .filter(e => e.date.startsWith(currentMonth))
        .reduce((sum, e) => sum + e.amount, 0);

      systemInstructionRef.current = `You are Arb-E, the official mascot and AI assistant for GreenTree Arboriculture. 
        You are "Always On" to answer questions about specific jobs, weather safety alerts (like 20mph wind gusts), and site navigation. 
        Your personality is professional, witty, and deeply knowledgeable about tree health, the Chattahoochee Valley, and Professional Arboriculture Standards. 
        Feel free to use tree/monkey nature puns playfully but remain helpful and professional.
        
        ${safetyContext}

        ${roleContext}
        
        Financial Data (Read-Only): The total overhead/expenses for the current month (${currentMonth}) is $${monthlyOverhead.toFixed(2)}.

        Currently active jobs: ${JSON.stringify(jobs.map(j => ({ client: j.clientName, address: j.address, status: j.status })))}`;
    }
  }, [isOpen, jobs, user, expenses]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  useEffect(() => {
    // Admin/Role needs re-authorization if not currently connected
    // Only show reauth prompt when we have a backend server (not static hosting)
    const isStaticHosting = typeof window !== 'undefined' && !window.location.hostname.includes('run.app');
    if (googleConnected === false && user && !messages.some(m => m.id === 'reauth-prompt') && !isStaticHosting) {
      // Set timeout so it doesn't instantly interrupt initial loading
      const timer = setTimeout(() => {
        setIsOpen(true);
        setMessages(prev => [...prev, {
          id: 'reauth-prompt',
          role: 'model',
          text: "I need a quick re-authorization to keep the ledger updated. Click here to refresh."
        }]);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [googleConnected, user]);

  useEffect(() => {
    const handleBrowserMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'PAYMENT_SUCCESSFUL') {
        setIsOpen(true);
        const { merchant, amount } = event.data.payload;
        setMessages(prev => [...prev, { 
          id: `payment-${Date.now()}`, 
          role: 'model', 
          text: `I see that ${merchant} payment went through. Should I add this $${amount.toFixed(2)} to the Financials Ledger?`,
          pendingExpense: { merchant, amount, category: 'Software/Subscription' } 
        }]);
      }
    };
    window.addEventListener('message', handleBrowserMessage);
    return () => window.removeEventListener('message', handleBrowserMessage);
  }, []);

  const handleConfirmExpense = (expenseInfo: any, messageId: string) => {
    if (onAddExpense) {
      onAddExpense({
        id: Math.random().toString(36).substr(2, 9),
        merchant: expenseInfo.merchant,
        amount: expenseInfo.amount,
        date: new Date().toISOString(),
        category: expenseInfo.category
      });
      
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, text: `✅ Added $${expenseInfo.amount.toFixed(2)} to the Financials Ledger under ${expenseInfo.merchant}.`, pendingExpense: undefined }
          : msg
      ));
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputVal.trim() || !systemInstructionRef.current) return;

    const userMsg = inputVal.trim();
    setInputVal('');
    setMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', text: userMsg }]);
    setIsTyping(true);

    try {
      const response = await fetch(apiUrl('/api/ai/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          history: messages
            .filter(m => m.role === 'user' || m.role === 'model')
            .map(m => ({ role: m.role, parts: [{ text: m.text }] })),
          model: 'gemini-3.6-flash',
          systemInstruction: systemInstructionRef.current
        })
      });
      const data = await response.json();
      setMessages(prev => [...prev, { id: Date.now().toString(), role: 'model', text: data.text || "Looks like I'm out on a limb right now with connection issues. Please try again!" }]);
    } catch (error) {
      console.error("Arb-E Chat Error:", error);
      setMessages(prev => [...prev, { id: Date.now().toString(), role: 'model', text: "Looks like I'm out on a limb right now with connection issues. Please try again!" }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="fixed bottom-8 right-8 z-[9999] flex flex-col items-end pointer-events-none">
      <div className="pointer-events-auto">
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 w-80 sm:w-96 mb-4 overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 transition max-h-[70vh]">
          <div className="bg-palm p-4 text-white flex justify-between items-center relative overflow-hidden">
            <div className="relative z-10 flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-full p-1 shadow-md">
                <ArbE mood="happy" className="w-full h-full" />
              </div>
              <div>
                <h3 className="font-bold">Arb-E <span className="text-xs bg-gecko px-2 py-0.5 rounded-full ml-1">Always On</span></h3>
                <p className="text-[10px] text-palm-100 uppercase tracking-widest">Certified AI Arborist</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition z-10 relative">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gecko rounded-full opacity-20 -translate-y-10 translate-x-10 pointer-events-none" />
          </div>

          <div className="h-96 p-4 overflow-y-auto bg-stone-50 space-y-4">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl p-3 text-sm ${msg.role === 'user' ? 'bg-palm text-white rounded-br-sm' : 'bg-white border border-stone-200 text-bark rounded-bl-sm shadow-sm'}`}>
                  {msg.text.includes('Click here to refresh') ? (
                    <span>
                      {msg.text.split('Click here to refresh')[0]}
                      <a href="/api/auth/google/url" rel="noopener noreferrer" onClick={(e) => {
                        e.preventDefault();
                        const authUrl = window.location.origin + '/api/auth/google/url';
                        fetch(apiUrl('/api/auth/google/url')).then(res => {
                          if (!res.ok) throw new Error('Server unavailable');
                          return res.json();
                        }).then(data => {
                          if (data.url) {
                            const width = 500, height = 600;
                            const left = window.screen.width / 2 - width / 2;
                            const top = window.screen.height / 2 - height / 2;
                            window.open(data.url, 'google_auth', `width=${width},height=${height},top=${top},left=${left}`);
                          }
                        }).catch(() => {
                          // Server not running — update message to reflect
                          setMessages(prev => prev.map(m => m.id === msg.id ? {
                            ...m,
                            text: "Google Workspace re-authorization requires the backend server. Please connect from the main dashboard or contact admin."
                          } : m));
                        });
                      }} className="text-gecko font-bold underline cursor-pointer hover:text-palm">Click here to refresh.</a>
                    </span>
                  ) : msg.text}
                  {msg.pendingExpense && (
                    <div className="mt-3 flex space-x-2">
                      <button 
                        onClick={() => handleConfirmExpense(msg.pendingExpense, msg.id)}
                        className="bg-gecko hover:bg-palm text-white px-3 py-1.5 rounded-lg font-bold text-xs transition active:scale-95"
                      >
                        Yes, Add to Ledger
                      </button>
                      <button 
                        onClick={() => {
                          setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, text: "Okay, I've ignored that transaction.", pendingExpense: undefined } : m));
                        }}
                        className="bg-stone-100 hover:bg-stone-200 text-stone-600 px-3 py-1.5 rounded-lg font-bold text-xs transition active:scale-95"
                      >
                        No, Skip
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 text-stone-500 rounded-2xl rounded-bl-sm p-4 w-16 shadow-sm flex space-x-1 items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-palm rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1.5 h-1.5 bg-palm rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1.5 h-1.5 bg-palm rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSend} className="p-3 bg-white border-t border-stone-100 relative">
            <input 
              type="text" 
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              placeholder="Ask about a job or weather..."
              className="w-full bg-stone-50 border border-stone-200 rounded-full pl-4 pr-12 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-palm/50 transition-shadow"
            />
            <button 
              type="submit" 
              disabled={!inputVal.trim() || isTyping}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-gecko text-white rounded-full flex items-center justify-center hover:bg-palm disabled:opacity-50 transition transform active:scale-95"
            >
              <svg className="w-4 h-4 translate-x-[1px] translate-y-[1px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
            </button>
          </form>
        </div>
      )}

      {/* Floating Button */}
      </div>
      <div className="pointer-events-auto">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-16 h-16 rounded-full shadow-2xl transition-transform duration-300 transform bg-white p-1 hover:scale-110 flex items-center justify-center border-2 border-gecko ${isOpen ? 'rotate-12 scale-110' : ''}`}
      >
        <ArbE mood="happy" className="w-full h-full" />
        {!isOpen && (
          <span className="absolute top-0 right-0 flex w-4 h-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-palm opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-palm border-2 border-white"></span>
          </span>
        )}
      </button>
      </div>
    </div>
  );
};

export default ArbEWidget;
